import { SalarySensitiveValue } from "@/components/sensitive-value/SalarySensitiveValue.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { PendingTableCell } from "@/pages/employees/employee/tables/PendingTableCell.tsx";
import {
  createExpandableTextCell,
  createHistoricalDateCell,
  createTextCell,
} from "@/pages/employees/employee/tables/table-cell-factory.utils.tsx";
import { EmployeeAdditionalCompensationEditModal } from "@/pages/employees/employee/widgets/compensation/EmployeeAdditionalCompensationEditModal.tsx";
import {
  useEmployeeAdditionalCompensationDelete,
  useEmployeeAdditionalCompensations,
} from "@/queries/employees/use-employee-additional-compensations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiEmployeeAdditionalCompensation,
  ApiEmployeeAdditionalCompensationPendingRow,
} from "@/types/employees/employee-additional-compensation.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

type AdditionalCompensationRow =
  | (ApiEmployeeAdditionalCompensation & { pending?: never })
  | ApiEmployeeAdditionalCompensationPendingRow;
const additionalCompensationColumnHelper = createColumnHelper<AdditionalCompensationRow>();

export const EmployeeWorkInfoAdditionalCompensations = ({
  employee,
  tableScrollBoundaryRef,
}: EmployeePageComponentProps) => {
  const { t } = useTranslation();
  const { canEditField, canViewField, canViewCustomTableFieldForEmployee } = usePermissions();
  const { renderDate, renderCurrency } = useFormatter();
  const { openModal } = useModals();
  const { table, fields } = useSystemTableConfig(
    ApiTableTypes.employee_additional_compensations,
    "view"
  );
  const { renderFieldDataInCell } = useCustomFieldsHook();

  const canViewAdditionalCompensation = useMemo(
    () => canViewField(employee, EmployeeFieldPermission.employee_additional_compensation),
    [employee, canViewField]
  );

  const canEditAdditionalCompensation = useMemo(
    () => canEditField(employee, EmployeeFieldPermission.employee_additional_compensation),
    [employee, canEditField]
  );

  const { data: employeeAdditionalCompensations, isLoading } = useEmployeeAdditionalCompensations(
    employee.id,
    canViewAdditionalCompensation
  );

  const employeeAdditionalCompensationDelete = useEmployeeAdditionalCompensationDelete();

  const lastCurrencyInData =
    employeeAdditionalCompensations?.items[employeeAdditionalCompensations.items.length - 1]
      ?.currency;

  const displayData = useMemo<AdditionalCompensationRow[]>(
    () => [
      ...(employeeAdditionalCompensations?.pending_rows ?? []),
      ...(employeeAdditionalCompensations?.items ?? []),
    ],
    [employeeAdditionalCompensations]
  );

  const handleEdit = useCallback(
    (employeeAdditionalCompensation?: ApiEmployeeAdditionalCompensation) => {
      openModal(EmployeeAdditionalCompensationEditModal, {
        employee,
        employeeAdditionalCompensation,
        lastCurrency: lastCurrencyInData,
      });
    },
    [employee, lastCurrencyInData, openModal]
  );

  const handleDeleteAction = useCallback(
    (employeeAdditionalCompensation: ApiEmployeeAdditionalCompensation) => {
      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          employeeAdditionalCompensationDelete.mutate(
            {
              employeeId: employee.id,
              employeeAdditionalCompensationId: employeeAdditionalCompensation.id,
            },
            {
              onSuccess: () => {
                analyticsService.trackEvent(
                  analyticEvents.employeeCompensations.additionalDeleted,
                  {
                    [analyticProperties.id]: employeeAdditionalCompensation.id,
                  }
                );
              },
            }
          );
        },
      });
    },
    [employee, openModal, employeeAdditionalCompensationDelete, t]
  );

  const columns = useMemo<ColumnDef<AdditionalCompensationRow, unknown>[]>(() => {
    const inner: ColumnDef<AdditionalCompensationRow, unknown>[] = [];

    const historicalDateCell = createHistoricalDateCell<AdditionalCompensationRow>({
      getValue: (comp) => renderDate(comp.date_effective_from),
      getClassName: (comp) => (comp.pending ? "text-secondary" : "text-title"),
      getDate: (comp) => comp.date_effective_from,
    });

    inner.push(
      additionalCompensationColumnHelper.display({
        header: t("common:date"),
        cell: (cell) => {
          const row = cell.row.original;
          return row.pending ? (
            <PendingTableCell pending={row.pending}>
              {renderDate(row.date_effective_from)}
            </PendingTableCell>
          ) : (
            historicalDateCell(cell)
          );
        },
        id: ApiFieldTypes.effective_from,
        ...ColumnSizes.historicalDate,
      })
    );

    inner.push(
      additionalCompensationColumnHelper.display({
        header: t("common:currency"),
        cell: createTextCell<AdditionalCompensationRow>({
          getValue: (comp) => comp.currency,
          getClassName: (comp) => (comp.pending ? "text-secondary" : "text-title"),
        }),
        id: ApiFieldTypes.employee_additional_compensation_currency,
        ...ColumnSizes.status,
      }),
      additionalCompensationColumnHelper.display({
        header: t("common:description"),
        cell: createTextCell<AdditionalCompensationRow>({
          getValue: (comp) => comp.description,
          getClassName: (comp) => (comp.pending ? "text-secondary" : "text-title"),
        }),
        id: ApiFieldTypes.employee_additional_compensation_description,
        ...ColumnSizes.standard,
      }),
      additionalCompensationColumnHelper.display({
        header: t("common:comments"),
        cell: createExpandableTextCell<AdditionalCompensationRow>({
          getValue: (comp) => comp.comment,
          getClassName: (comp) => (comp.pending ? "text-secondary" : "text-title"),
        }),
        id: ApiFieldTypes.employee_additional_compensation_comment,
        ...ColumnSizes.standard,
      })
    );

    inner.push(
      additionalCompensationColumnHelper.display({
        header: t("core:compensation_type"),
        cell: createTextCell<AdditionalCompensationRow>({
          getValue: (comp) => comp.compensation_type.title,
          getClassName: (comp) => (comp.pending ? "text-secondary" : "text-title"),
        }),
        id: ApiFieldTypes.employee_additional_compensation_compensation_type,
        ...ColumnSizes.standard,
      })
    );

    inner.push(
      additionalCompensationColumnHelper.display({
        header: t("core:amount_money_gross"),
        cell: (cell) => (
          <SalarySensitiveValue
            value={
              cell.row.original.amount_gross == null
                ? "-"
                : renderCurrency(cell.row.original.amount_gross, cell.row.original.currency)
            }
            textClassName={cell.row.original.pending ? "text-secondary" : "text-title"}
          />
        ),
        id: ApiFieldTypes.employee_additional_compensation_amount_gross,
        ...ColumnSizes.status,
      })
    );

    inner.push(
      additionalCompensationColumnHelper.display({
        header: t("core:amount_money_net"),
        cell: (cell) => (
          <SalarySensitiveValue
            value={
              cell.row.original.amount_net == null
                ? "-"
                : renderCurrency(cell.row.original.amount_net, cell.row.original.currency)
            }
            textClassName={cell.row.original.pending ? "text-secondary" : "text-title"}
          />
        ),
        id: ApiFieldTypes.employee_additional_compensation_amount_net,
        ...ColumnSizes.status,
      })
    );

    return fields.flatMap((field) => {
      if (field.type === ApiFieldTypes.custom) {
        if (!table || !canViewCustomTableFieldForEmployee(employee, table.id, field.slug)) {
          return [];
        }
        return [
          additionalCompensationColumnHelper.display({
            header: field.title,
            cell: ({ row }) => renderFieldDataInCell(field, row.original.custom_fields ?? []),
            id: field.slug,
            ...ColumnSizes.standard,
          }),
        ];
      }
      if (field.type === ApiFieldTypes.changed_by && canViewAdditionalCompensation) {
        return [
          additionalCompensationColumnHelper.display({
            header: field.title?.trim() || t("core:changed_by"),
            cell: ({ row }) =>
              row.original.changed_by ? (
                <EmployeeHoverCard
                  employee={row.original.changed_by}
                  position={getEmployeePositionTitle(row.original.changed_by)}
                >
                  <EmployeeItem employee={row.original.changed_by} showPosition={false} size="sm" />
                </EmployeeHoverCard>
              ) : null,
            id: field.type,
            ...ColumnSizes.standard,
          }),
        ];
      }
      const column = inner.find((item) => item.id === field.type);
      const fallbackTitle = typeof column?.header === "string" ? column.header : "";
      return column ? [{ ...column, header: field.title?.trim() || fallbackTitle }] : [];
    });
  }, [
    canViewAdditionalCompensation,
    fields,
    renderCurrency,
    renderFieldDataInCell,
    renderDate,
    t,
    table,
    canViewCustomTableFieldForEmployee,
    employee,
  ]);

  const renderRowAction = useCallback(
    (row: Row<AdditionalCompensationRow>) => {
      if (row.original.pending) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => handleEdit(row.original as ApiEmployeeAdditionalCompensation)}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => handleDeleteAction(row.original as ApiEmployeeAdditionalCompensation)}
            variant="menuDanger"
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [handleEdit, handleDeleteAction, t]
  );

  const displayCount = useMemo(() => {
    return displayData.length;
  }, [displayData.length]);

  return (
    <Stack gap="lg">
      <Stack
        className="justify-between"
        direction="horizontal"
        items="center"
        gap="md"
        data-employee-table-heading
      >
        <Title
          header={t("core:additional_compensation")}
          size="lg"
          badge={displayCount > 1 ? `(${displayCount})` : undefined}
          badgeVariant="secondary"
        />
        {canEditAdditionalCompensation && (
          <Button
            onClick={() => handleEdit()}
            variant="secondary"
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:add")}
          </Button>
        )}
      </Stack>

      <HistoryTable<AdditionalCompensationRow>
        historyItems={displayData}
        columns={columns}
        canViewHistory={true}
        isLoading={isLoading}
        isFetching={employeeAdditionalCompensationDelete.isPending}
        emptyStateTitle={t("core:additional_compensation_empty_state_title")}
        scrollLayout="notion"
        scrollBoundaryRef={tableScrollBoundaryRef}
        stickyLastColumn={false}
        renderRowAction={canEditAdditionalCompensation ? renderRowAction : undefined}
      />
    </Stack>
  );
};
